/**
 * WHO does this — a vendor, TKC itself, or a name nobody has made a vendor yet
 * (O-24, docs/17 wave 4.2, docs/04 §3k).
 *
 * Matt: "convert it now but still allow the user to also select 'TKC Internal'
 * or type free text — this needs some flexibility... ensure that it allows the
 * user to search in the text box to quickly find a vendor or TKC Internal."
 *
 * ─── WHY THIS IS NOT A FLAG ON `SearchSelect` ─────────────────────────────
 *
 * `SearchSelect` refuses free text on purpose, and its own header says why: a
 * control that quietly kept an unmatched string would be a text field wearing a
 * picker's clothes. That rule is worth keeping, so free text here is not a
 * fallback that happens when nothing matches — it is an OPTION, spelled out
 * (`Use "Acme Guarding" as free text`) and picked deliberately. A typo still
 * cannot become a vendor name by accident, and it still cannot become a value
 * just because somebody stopped typing and clicked away.
 *
 * That is also why this composes `SearchSelect` rather than reimplementing it:
 * one combobox, one keyboard model, one set of ARIA wiring — and the search box
 * Matt asked for searches vendors and "TKC Internal" together, because they are
 * options in the same list rather than two controls to choose between.
 *
 * ─── FOUR STATES, AND THE FOURTH IS NOT THE THIRD ─────────────────────────
 *
 * null is NOT STATED, and it is offered as "Not stated" rather than left as the
 * absence of a choice, because "nobody has said" and "we do it ourselves" are
 * different answers and a building where nobody has recorded who monitors the
 * alarm should not read as though TKC does.
 */

import { PARTY_INTERNAL_LABEL, type PartyRef, type PartyRefWrite } from '@propos/shared';
import { useState, type ReactNode } from 'react';

import { SearchSelect, type SearchSelectOption } from './SearchSelect.js';

export interface PartyVendorOption {
  readonly id: string;
  readonly name: string;
  /** A second line telling two similarly-named vendors apart. */
  readonly detail?: string | undefined;
}

/** The option value encoding. One place, so parse and print cannot disagree. */
const INTERNAL_VALUE = 'internal';
const VENDOR_PREFIX = 'vendor:';
const TEXT_PREFIX = 'text:';

function toValue(ref: PartyRef | null): string | null {
  if (ref === null) return null;
  switch (ref.kind) {
    case 'internal':
      return INTERNAL_VALUE;
    case 'vendor':
      return ref.vendorId === null ? null : `${VENDOR_PREFIX}${ref.vendorId}`;
    case 'text':
      return ref.text === null ? null : `${TEXT_PREFIX}${ref.text}`;
  }
}

function fromValue(value: string | null): PartyRefWrite | null {
  if (value === null) return null;
  if (value === INTERNAL_VALUE) return { kind: 'internal' };
  if (value.startsWith(VENDOR_PREFIX)) {
    return { kind: 'vendor', vendorId: value.slice(VENDOR_PREFIX.length) };
  }
  const text = value.slice(TEXT_PREFIX.length).trim();
  return text === '' ? null : { kind: 'text', text };
}

export function PartyPicker({
  label,
  value,
  vendors,
  onChange,
  hint,
  error,
  vendorsComplete,
}: {
  readonly label: string;
  readonly value: PartyRef | null;
  /** Active SERVICE vendors. Utility vendors are a different kind of thing
   *  (0054) and the database refuses them here, so they are never offered. */
  readonly vendors: readonly PartyVendorOption[];
  readonly onChange: (next: PartyRefWrite | null) => void;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  /**
   * TRUE only when `vendors` is the WHOLE set of active service vendors —
   * loaded, not failed, not truncated (RM-020).
   *
   * Free text is offered only when this holds, and that is the whole point:
   * "not in the list" only means "not a vendor" if the list is the list. Over
   * a partial catalogue the option would invite storing a real vendor's name
   * as a duplicate string, which is exactly the unlinked data this field
   * exists to stop.
   */
  readonly vendorsComplete: boolean;
}): ReactNode {
  const [search, setSearch] = useState('');

  const typed = search.trim();
  const options: SearchSelectOption[] = [
    { value: INTERNAL_VALUE, label: PARTY_INTERNAL_LABEL, detail: 'TKC handles this' },
    ...vendors.map((vendor) => ({
      value: `${VENDOR_PREFIX}${vendor.id}`,
      label: vendor.name,
      detail: vendor.detail ?? 'Vendor',
    })),
  ];

  /**
   * The stored value is kept as an option even when it is free text or a vendor
   * that is no longer active — otherwise opening the form on an old record
   * would show an empty picker over a value that IS set, and saving would
   * silently erase it.
   */
  const current = toValue(value);
  if (current !== null && !options.some((option) => option.value === current)) {
    options.unshift({
      value: current,
      label:
        value?.kind === 'vendor'
          ? (value.vendorName ?? 'Unknown vendor')
          : (value?.text ?? PARTY_INTERNAL_LABEL),
      detail: value?.kind === 'text' ? 'Free text' : 'Currently set',
    });
  }

  // Offered only when it would not duplicate a real match, so the list does not
  // suggest typing "Acme Security" as free text while Acme Security sits above
  // it as a vendor.
  const duplicates = options.some((option) => option.label.toLowerCase() === typed.toLowerCase());
  if (typed !== '' && !duplicates && vendorsComplete) {
    options.push({
      value: `${TEXT_PREFIX}${typed}`,
      label: `Use “${typed}” as free text`,
      detail: 'Not a vendor record',
    });
  }

  return (
    <SearchSelect
      label={label}
      value={current}
      options={options}
      onChange={(next) => {
        onChange(fromValue(next));
      }}
      onSearchChange={setSearch}
      placeholder={vendorsComplete ? 'Search vendors, or type a name' : 'Loading vendors…'}
      hint={hint}
      error={error}
      clearLabel="Not stated"
      emptyLabel={
        vendorsComplete
          ? 'No vendor matches — keep typing to use it as free text'
          : 'Vendors are still loading — reload before entering a name by hand'
      }
    />
  );
}
